'use client';

import { useSession } from "next-auth/react"
import { useRouter } from "next/navigation"
import { useEffect } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { TrendingUp, FileText, Sparkles, Target, Search, Zap, Users, BarChart3, Clock, Shield } from "lucide-react"
import LoginButton from "@/components/auth/LoginButton"
import Link from "next/link"

export default function LoginPage() {
  const { data: session, status } = useSession()
  const router = useRouter()

  useEffect(() => {
    if (session) {
      router.push('/')
    }
  }, [session, router])

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        
        {/* Hero Section */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <TrendingUp className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
          </div>
          
          <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4">
            Level the Playing Field for 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Small Cap Trading</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            AI-powered research for the stocks institutions ignore. Never miss a news-driven catalyst again with our intelligent ASX announcement analyzer.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Badge className="bg-green-100 text-green-800 text-base px-4 py-2 font-semibold">
              🎉 50 FREE AI Searches/Month
            </Badge>
            <Badge variant="outline" className="text-base px-4 py-2">
              No Credit Card Required
            </Badge>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          
          {/* Left Column - Value Propositions */}
          <div className="space-y-8">
            
            {/* Small Cap Advantage */}
            <Card className="border-0 bg-white/80 backdrop-blur-md shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Where AI Research Delivers Maximum Impact</h3>
                    <p className="text-gray-600 mb-4">
                      Small cap stock prices are driven by <strong>news flow and catalysts</strong>, not fundamental models. A single announcement can move prices 20-50% in one day.
                    </p>
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <p className="text-sm text-orange-800 font-medium">
                        💡 Perfect for catalyst-driven trading strategies where timing is everything
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Research Coverage Gap */}
            <Card className="border-0 bg-white/80 backdrop-blur-md shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Fill the Research Coverage Gap</h3>
                    <p className="text-gray-600 mb-4">
                      Investment banks don't cover small caps. While <strong>large caps have armies of analysts</strong>, small caps operate in an information vacuum.
                    </p>
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-sm text-purple-800 font-medium">
                        🔍 Access insights that institutional investors miss
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* How It Works */}
            <Card className="border-0 bg-white/80 backdrop-blur-md shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="w-6 h-6 text-blue-600" />
                  How It Works
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm">1</div>
                    <span className="text-gray-700">ASX announcements are automatically processed</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-sm">2</div>
                    <span className="text-gray-700">AI transforms PDFs into searchable insights</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-sm">3</div>
                    <span className="text-gray-700">Get instant, contextual answers to your queries</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="border-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                <CardContent className="p-4 text-center">
                  <BarChart3 className="w-8 h-8 mx-auto mb-2" />
                  <div className="text-xl font-bold">Full History</div>
                  <div className="text-sm opacity-90">All ASX Companies</div>
                </CardContent>
              </Card>
              <Card className="border-0 bg-gradient-to-r from-green-500 to-blue-600 text-white">
                <CardContent className="p-4 text-center">
                  <Clock className="w-8 h-8 mx-auto mb-2" />
                  <div className="text-xl font-bold">Intra-day</div>
                  <div className="text-sm opacity-90">Updates</div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Column - Login */}
          <div className="space-y-8">
            
            {/* Login Card */}
            <Card className="border-0 bg-white/90 backdrop-blur-md shadow-2xl sticky top-8">
              <CardHeader className="text-center pb-4">
                <h2 className="text-2xl font-bold text-gray-900">Get Started Free</h2>
                <p className="text-gray-600">
                  Join traders who use AI to stay ahead of the market
                </p>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <LoginButton 
                  className="w-full mb-6" 
                  size="lg"
                />
                
                {/* Features checklist */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-xs">✓</span>
                    </div>
                    <span><strong>50 free AI searches</strong> per month</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-xs">✓</span>
                    </div>
                    <span>Search <strong>all ASX announcements</strong></span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-xs">✓</span>
                    </div>
                    <span><strong>AI-powered</strong> insights and analysis</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-xs">✓</span>
                    </div>
                    <span>Save and organize your <strong>research queries</strong></span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-xs">✓</span>
                    </div>
                    <span><strong>Intra-day</strong> processing of new announcements</span>
                  </div>
                </div>

                <div className="text-center">
                  <div className="bg-blue-50 p-3 rounded-lg mb-4">
                    <div className="flex items-center justify-center gap-2">
                      <Shield className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-800 font-medium">Secure OAuth Login</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Example Queries */}
            <Card className="border-0 bg-white/60 backdrop-blur-sm">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-purple-600" />
                  Example Searches
                </h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700 space-y-3">
                    <p className="font-medium text-gray-800">
                      "You are an expert mining industry analyst tasked with extracting key information from exploration company announcements."
                    </p>
                    
                    <p>
                      <strong>Extract the following in structured format:</strong>
                    </p>
                    
                    <div className="space-y-2 pl-4 border-l-2 border-gray-300">
                      <p><strong>DRILLING CAMPAIGN:</strong> Campaign details, drilling progress, timeline updates, significant results</p>
                      <p><strong>ASSAY RESULTS:</strong> Sample intervals, grades, laboratory status, significant intersections</p>
                      <p><strong>REGULATORY:</strong> Permit status, environmental compliance, land access agreements</p>
                      <p><strong>FINANCIAL:</strong> Budget information, operational changes, strategic partnerships</p>
                      <p><strong>GEOLOGICAL:</strong> Updated models, exploration targets, metallurgical studies</p>
                      <p><strong>MARKET SENSITIVE:</strong> Material changes, risk factors, forward guidance</p>
                    </div>
                    
                    <p>
                      <strong>Instructions:</strong> Extract factual data only, preserve numerical precision, distinguish preliminary vs final results, flag uncertainties."
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-blue-800 font-medium text-sm">📊 See it in action:</span>
                    </div>
                    <p className="text-sm text-blue-700 mb-2">
                      View a real example of our AI analyzing a company's drilling and exploration update
                    </p>
                    <a 
                      href="https://www.asx-genai-insights.com.au/p/lgbTutUsPBg-K_" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                    >
                      View Example Analysis →
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-16 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-4">
            Transform your small cap trading with AI-powered research that institutional investors don't have access to.
          </p>
          <p className="text-xs text-gray-500">
            By signing in, you agree to our{' '}
            <Link href="/terms" className="text-blue-600 hover:text-blue-800 underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href="/privacy" className="text-blue-600 hover:text-blue-800 underline">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}